/**
 * DesignFlow — the data-driven design phase.
 *
 * Fetches the customer-safe question screens for the service line, walks them
 * in order, and renders each via the generic QuestionScreen. Conditional-reveal
 * screens (parent gate) are skipped unless their parent option is selected.
 *
 * Progress counts to the estimate: "N of <gating screens>". At the end, routes
 * to the range/spec.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getQuestions,
  createEstimateFromAnswers,
  loadSession,
  cancelSession,
  type CustomerQuestionScreen,
  type RawAnswer,
} from '../../lib/api';
import { DataFlowProvider, useDataFlow } from '../../lib/dataFlowSession';
import { QuestionScreen } from '../../components/QuestionScreen';

interface FlowState {
  serviceLine?: string;
  productCode?: string;
  /** Project loop: the visit this room belongs to. */
  projectId?: string;
  /** Project loop: labels for every room in the visit. */
  roomLabels?: string[];
  /** Project loop: which room we're on (0-based). */
  roomIndex?: number;
  /**
   * Project loop: per-room service line / product code, parallel to roomLabels.
   * Present when rooms differ (e.g. paint Interior then Exterior). When absent,
   * every room uses the top-level serviceLine/productCode (e.g. many bathrooms).
   */
  roomServiceLines?: string[];
  roomProductCodes?: string[];
  /** The project's customer-chosen name (carried for the summary/labels). */
  projectName?: string;
  /** Brand-new project — start clean, never resume a stale draft. */
  fresh?: boolean;
  /** Reopening an OPEN project — resume the saved draft for this room. */
  resume?: boolean;
}

/** Human label for the section header, e.g. PAINT_INT → "Interior paint". */
function sectionLabelFor(serviceLine: string): string {
  switch (serviceLine) {
    case 'BATHROOM':
      return 'Bathroom';
    case 'KITCHEN':
      return 'Kitchen';
    case 'PAINT_INT':
      return 'Interior paint';
    case 'PAINT_EXT':
      return 'Exterior paint';
    default:
      return '';
  }
}

/** A screen is revealed if it has no parent gate, or its parent option is chosen. */
function isRevealed(
  screen: CustomerQuestionScreen,
  answerFor: (code: string) => { selectedOptionCodes?: string[] } | undefined,
): boolean {
  // A screen's parent gate lives on its (first) question.
  const q = screen.questions[0];
  if (!q?.parentQuestionCode || !q.parentOptionCode) return true;

  const parentAns = answerFor(q.parentQuestionCode);
  const chosen = new Set(parentAns?.selectedOptionCodes ?? []);
  const gate = q.parentOptionCode;

  if (gate === '__ANY__') return chosen.size > 0;
  if (gate.includes(',')) return gate.split(',').some((c) => chosen.has(c));
  return chosen.has(gate);
}

interface LoopContext {
  projectId: string;
  roomLabels: string[];
  roomIndex: number;
  serviceLine: string;
  productCode: string;
  /** Per-room service lines/product codes when rooms differ (paint both). */
  roomServiceLines?: string[];
  roomProductCodes?: string[];
}

function FlowInner({
  screens,
  startIndex,
  loop,
  sectionLabel,
}: {
  screens: CustomerQuestionScreen[];
  startIndex: number;
  /** Present when this flow is one room of a multi-room project. */
  loop?: LoopContext;
  sectionLabel?: string;
}): JSX.Element {
  const navigate = useNavigate();
  const flow = useDataFlow();
  const [index, setIndex] = useState(startIndex);

  // Screens visible given current answers (conditional reveals resolved).
  const visible = useMemo(
    () => screens.filter((s) => isRevealed(s, flow.getAnswer)),
    [screens, flow],
  );

  const total = visible.length;
  const screen = visible[Math.min(index, total - 1)];
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  // Multi-bathroom loop: show a short "now scoping <this bathroom>" intro before
  // the questions so the customer knows which bathroom this set is for. Only when
  // the project has more than one room and we're on a bathroom.
  const isMultiRoom = !!loop && loop.roomLabels.length > 1;
  const roomLabel = loop?.roomLabels[loop.roomIndex];
  const [introDone, setIntroDone] = useState(false);
  if (isMultiRoom && !introDone) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="progress__count">
            {`BATHROOM ${(loop?.roomIndex ?? 0) + 1} OF ${loop?.roomLabels.length}`}
          </div>
          <h1>Now scoping: {roomLabel}</h1>
          <p className="stage-card__lede">
            Answer the next questions for <strong>{roomLabel}</strong>. When you&rsquo;re done we&rsquo;ll
            move to the next one.
          </p>
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg pb-btn--next" onClick={() => setIntroDone(true)}>
              Start {roomLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  async function finish(): Promise<void> {
    setFinishing(true);
    setFinishError(null);
    try {
      const snapshotId = await flow.ensureSnapshot();
      const label = loop?.roomLabels[loop.roomIndex];
      const estimate = await createEstimateFromAnswers({
        snapshotId,
        serviceLine: flow.serviceLine,
        productCode: flow.productCode,
        answers: flow.allAnswers(),
        ...(loop ? { projectId: loop.projectId } : {}),
        ...(label ? { label } : {}),
      });

      // If more rooms remain (paint both / multi-bathroom), hop to the brief
      // "part captured" screen which offers the next room. Otherwise this is the
      // last/only room → go STRAIGHT to the project step (budget → timeline →
      // scope review → submit). No scope screen appears before budget/timeline.
      const hasMoreRooms = !!loop && loop.roomIndex + 1 < loop.roomLabels.length;
      if (hasMoreRooms) {
        navigate('/design/estimate', { state: { estimate, loop } });
      } else if (loop) {
        navigate('/design/project', { state: { projectId: loop.projectId } });
      } else {
        // No project context (shouldn't happen now) — fall back to SpecReady.
        navigate('/design/estimate', { state: { estimate } });
      }
    } catch {
      setFinishError('We couldn\u2019t finalise your estimate just now. Try again in a moment.');
      setFinishing(false);
    }
  }

  if (!screen) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>That&rsquo;s everything we need.</h1>
          <p className="stage-card__lede">We&rsquo;re putting your scope together.</p>
          {finishError ? (
            <div className="pb-note pb-note--flag" role="alert">
              {finishError}
            </div>
          ) : null}
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => void finish()} disabled={finishing}>
              {finishing ? 'Finalising\u2026' : 'See your estimate'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function next(): void {
    if (index + 1 >= total) void finish();
    else {
      const nextIndex = index + 1;
      setIndex(nextIndex);
      flow.autosave(nextIndex); // persist position + answers on advance
    }
  }

  function back(): void {
    const prev = index - 1;
    setIndex(prev);
    flow.autosave(prev);
  }

  function saveExit(): void {
    flow.autosave(index); // persist current position + answers
    navigate('/dashboard');
  }

  function cancel(): void {
    // Discard the draft, then return to the dashboard.
    void cancelSession(
      flow.serviceLine,
      loop ? { projectId: loop.projectId, roomIndex: loop.roomIndex } : undefined,
    ).catch(() => {
      /* best-effort */
    });
    navigate('/dashboard');
  }

  return (
    <QuestionScreen
      screen={screen}
      step={index + 1}
      totalSteps={total}
      onNext={next}
      onBack={index > 0 ? back : undefined}
      onSaveExit={saveExit}
      onCancel={cancel}
      {...(sectionLabel ? { sectionLabel } : {})}
    />
  );
}

export function DesignFlow(): JSX.Element {
  const location = useLocation();
  const state = (location.state as FlowState) ?? {};
  const roomIndex = state.roomIndex ?? 0;

  // The current room's service line: prefer the per-room array (paint both),
  // else the top-level value (single flow / same-line loop like many bathrooms).
  const serviceLine = state.roomServiceLines?.[roomIndex] ?? state.serviceLine ?? 'BATHROOM';
  const productCode = state.roomProductCodes?.[roomIndex] ?? state.productCode ?? 'BATH_FULL';

  // Project loop context (present when quoting multiple rooms in one visit).
  const loop =
    state.projectId && state.roomLabels
      ? {
          projectId: state.projectId,
          roomLabels: state.roomLabels,
          roomIndex,
          serviceLine,
          productCode,
          ...(state.roomServiceLines ? { roomServiceLines: state.roomServiceLines } : {}),
          ...(state.roomProductCodes ? { roomProductCodes: state.roomProductCodes } : {}),
        }
      : undefined;

  const sectionLabel = sectionLabelFor(serviceLine);

  const [screens, setScreens] = useState<CustomerQuestionScreen[] | null>(null);
  const [resumed, setResumed] = useState<{ answers: RawAnswer[]; screenIndex: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // Resume a saved draft ONLY when explicitly reopening (state.resume), keyed by
    // this project + room. A brand-new project (fresh) or a new room always starts
    // clean — this is what stops a new paint project from rehydrating an old draft.
    const sessionP =
      state.resume && loop
        ? loadSession(serviceLine, { projectId: loop.projectId, roomIndex })
        : Promise.resolve(null);
    Promise.all([getQuestions(serviceLine), sessionP])
      .then(([q, saved]) => {
        if (!active) return;
        setScreens(q.screens);
        if (saved && saved.answers.length > 0) {
          setResumed({ answers: saved.answers, screenIndex: saved.currentScreenIndex });
        }
        setReady(true);
      })
      .catch(() => {
        if (active) setError('We couldn\u2019t load the questions just now.');
      });
    return () => {
      active = false;
    };
    // roomIndex in the dep list so navigating to the next room reloads clean.
  }, [serviceLine, loop?.projectId, loop?.roomIndex]);

  if (error) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="pb-note pb-note--flag" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }
  if (!ready || !screens) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <p className="stage-card__lede">Loading your questions&hellip;</p>
        </div>
      </div>
    );
  }

  return (
    <DataFlowProvider
      // Remount per room so each room starts with fresh answers/snapshot.
      key={loop ? `${loop.projectId}:${loop.roomIndex}` : 'single'}
      serviceLine={serviceLine}
      productCode={productCode}
      initialAnswers={resumed?.answers}
      {...(loop ? { projectId: loop.projectId, roomIndex: loop.roomIndex } : {})}
    >
      <FlowInner
        screens={screens}
        startIndex={resumed?.screenIndex ?? 0}
        loop={loop}
        {...(sectionLabel ? { sectionLabel } : {})}
      />
    </DataFlowProvider>
  );
}
